import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';

import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { TagServicoTomadoService } from '../../../../../shared/services/apis/repo-dfse/tag-servico-tomado.service';
import { TagServicoTomadoItemStruct } from '../../../../../shared/services/structs/repo-dfse/tag-servico-tomado-item.struct';
import { ServicoTomadoBulkTagRequest } from '../../../../../shared/services/requests/repo-dfse/servico-tomado-bulk-tag.request';

export interface AlterarTagsDialogData {
  idsNFSeEntrada: number[];
  tags: TagServicoTomadoItemStruct[];
  idsTagsAtuais?: number[];
}

@Component({
  selector: 'app-st-alterar-tags-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './alterar-tags-dialog.component.html',
  styleUrls: ['./alterar-tags-dialog.component.css'],
})
export class AlterarTagsDialogComponent implements OnInit {
  public modo: string = 'incluir';
  public searchText: string = '';
  public tagsSelecionadas: Set<number> = new Set();
  public tagsFiltradas: TagServicoTomadoItemStruct[] = [];
  public isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<AlterarTagsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AlterarTagsDialogData,
    private tagServicoTomadoService: TagServicoTomadoService,
    private alertService: AlertService
  ) { }

  ngOnInit(): void {
    this.tagsFiltradas = [...this.data.tags];
    this.tagsSelecionadas = new Set(this.data.idsTagsAtuais ?? []);
  }

  public filtrarTags(): void {
    const termo = this.searchText.toLowerCase().trim();
    if (!termo) {
      this.tagsFiltradas = [...this.data.tags];
    } else {
      this.tagsFiltradas = this.data.tags.filter(t => t.descricao.toLowerCase().includes(termo));
    }
  }

  public toggleTag(id: number): void {
    if (this.tagsSelecionadas.has(id)) {
      this.tagsSelecionadas.delete(id);
    } else {
      this.tagsSelecionadas.add(id);
    }
  }

  public isTagSelecionada(id: number): boolean {
    return this.tagsSelecionadas.has(id);
  }

  public get contadorText(): string {
    const count = this.tagsSelecionadas.size;
    if (count === 0) return '';
    return count === 1 ? '1 tag selecionada' : `${count} tags selecionadas`;
  }

  public get helpText(): string {
    if (this.modo === 'incluir') {
      return 'As tags selecionadas serão adicionadas, mantendo as existentes.';
    }
    return 'As tags selecionadas serão removidas, mantendo as demais existentes.';
  }

  public get podeAplicar(): boolean {
    return this.tagsSelecionadas.size > 0 && !this.isLoading;
  }

  public onModoChange(): void {
    this.tagsSelecionadas.clear();
  }

  public salvar(): void {
    if (!this.podeAplicar) return;

    this.isLoading = true;

    const request: ServicoTomadoBulkTagRequest = {
      idsNFSeEntrada: this.data.idsNFSeEntrada,
      idsTag: Array.from(this.tagsSelecionadas),
      isIncluir: this.modo === 'incluir',
    };

    this.tagServicoTomadoService.bulkUpdateVinculos(request).subscribe({
      next: res => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        const mensagem = this.modo === 'incluir' ? 'Tags incluídas com sucesso!' : 'Tags removidas com sucesso!';
        this.alertService.show('Sucesso', mensagem, AlertType.success);
        this.dialogRef.close({ success: true });
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Falha ao atualizar as tags.', AlertType.error);
      },
    });
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
